import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { auth as apiAuth } from '../services/api';

interface User {
  id: string;
  email: string;
  isPremium: boolean;
  isAdmin?: boolean;
  plan?: string;
  maxJobs?: number;
  suspended?: boolean;
  // User settings
  emailNotifications?: boolean;
  slackNotifications?: boolean;
  defaultMethod?: string;
  defaultTimeout?: string;
  requireHttps?: boolean;
  verifySsl?: boolean;
  planMinSeconds?: number;
  planExpiresAt?: string;
  planDuration?: 'month' | 'year';
  roles?: string[];
  autoDisableOnFailure?: boolean;
}

interface AuthContextType {
  isAuthenticated: boolean;
  user: User | null;
  loading: boolean;
  error: string | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  register: (email: string, password: string, planId?: string, duration?: 'month' | 'year') => Promise<void>;
  forgotPassword: (email: string) => Promise<void>;
  resetPassword: (token: string, password: string) => Promise<void>;
  updateUser: (user: User) => void;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  isAuthenticated: false,
  user: null,
  loading: true,
  error: null,
  login: async () => {},
  logout: () => {},
  register: async () => {},
  forgotPassword: async () => {},
  resetPassword: async () => {},
  updateUser: () => {},
  refreshUser: async () => {},
});

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [initialized, setInitialized] = useState(false);

  const initializeAuth = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setLoading(false);
        setInitialized(true);
        return;
      }

      const response = await apiAuth.getMe();
      setUser(response.data);
      setIsAuthenticated(true);
    } catch (err: any) {
      console.error('Auth initialization error:', err);
      localStorage.removeItem('token');
      setError(err?.response?.data?.message || err.message || 'Authentication failed');
    } finally {
      setLoading(false);
      setInitialized(true);
    }
  }, []);

  useEffect(() => {
    initializeAuth();
  }, [initializeAuth]);

  const login = async (email: string, password: string) => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiAuth.login(email, password);
      const { token, user } = response.data;
      if (user.suspended) {
        throw new Error('Your account is suspended.');
      }
      localStorage.setItem('token', token);
      setIsAuthenticated(true);
      setUser(user);
    } catch (err: any) {
      setIsAuthenticated(false);
      setUser(null);
      setError(err?.response?.data?.message || err.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const logout = useCallback(() => {
    localStorage.removeItem('token');
    setIsAuthenticated(false);
    setUser(null);
    setError(null);
  }, []);

  useEffect(() => {
    if (user?.suspended) {
      logout();
      window.location.href = '/login?suspended=1';
    }
  }, [user, logout]);

  const register = async (email: string, password: string, planId?: string, duration?: 'month' | 'year') => {
    setLoading(true);
    setError(null);
    try {
      await apiAuth.register(email, password, planId, duration);
    } catch (err: any) {
      setError(err?.response?.data?.message || err.message || 'Registration failed');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const forgotPassword = async (email: string) => {
    setLoading(true);
    setError(null);
    try {
      await apiAuth.forgotPassword(email);
    } catch (err: any) {
      setError(err?.response?.data?.message || err.message || 'Password reset request failed');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const resetPassword = async (token: string, password: string) => {
    setLoading(true);
    setError(null);
    try {
      await apiAuth.resetPassword(token, password);
    } catch (err: any) {
      setError(err?.response?.data?.message || err.message || 'Password reset failed');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const updateUser = useCallback((updated: User) => {
    setUser(updated);
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const response = await apiAuth.getMe();
      setUser(response.data);
    } catch (err: any) {
      setUser(null);
      setIsAuthenticated(false);
    }
  }, []);

  if (!initialized) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  const value = {
    isAuthenticated,
    user,
    loading,
    error,
    login,
    logout,
    register,
    forgotPassword,
    resetPassword,
    updateUser,
    refreshUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}; 